import React, { useState, useEffect } from 'react';
import {
  ALL_HOMEBREW_KEYS,
  DEFAULT_TEST_PRESET_CATEGORIES,
  FEATURES,
  HOMEBREW_GROUPS,
  TEST_PRESET_CATEGORY_OPTIONS,
} from './forge-v2/constants';

// DM-only editor for the campaign metadata that drives the dashboard's
// presentation: schedule, blurb, current quest, and session count.
// Phase II / Phase B (2026-05-17). Opened from the ⚙ Settings button on
// CampaignHeroStrip (V1) and CalmCampaignHero (V2). Submits through the
// onSave callback, which the mount layer wires to
// api.campaigns.updateCampaignSettings.
//
// Field limits mirror the server-side normalizer in updateCampaignSettingsImpl
// — schedule 80, blurb 600, quest 200, sessionCount 0–100000.

const MAX_SCHEDULE = 80;
const MAX_TAGLINE = 120;
const MAX_BLURB = 600;
const MAX_QUEST = 200;

const DEFAULT_FEATURES = Object.fromEntries(FEATURES.map((feature) => [feature.key, true]));
const DEFAULT_HOMEBREW = Object.fromEntries(ALL_HOMEBREW_KEYS.map((key) => [key, true]));
const EMPTY_TEST_CHARACTERS = [];

const normalizeFeatureState = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return Object.fromEntries(FEATURES.map((feature) => [feature.key, source[feature.key] !== false]));
};

const normalizeHomebrewState = (value) => {
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    return { ...DEFAULT_HOMEBREW };
  }
  return Object.fromEntries(ALL_HOMEBREW_KEYS.map((key) => [key, value[key] === true]));
};

const normalizeTestPresetCategoryState = (value) => ({
  ...DEFAULT_TEST_PRESET_CATEGORIES,
  ...(value && typeof value === 'object' ? value : {}),
});

const deriveInitialTestPresetCategories = (testCharacters) => {
  const firstWithCategories = (testCharacters || [])
    .find((character) => character?.testPresetCategories && typeof character.testPresetCategories === 'object');
  return normalizeTestPresetCategoryState(firstWithCategories?.testPresetCategories);
};

const fieldStyle = (theme) => ({
  width: '100%',
  background: 'rgba(0,0,0,0.32)',
  border: `1px solid ${theme.colors.borderLight}`,
  color: theme.colors.textPrimary,
  borderRadius: theme.effects?.borderRadiusSm || '4px',
  padding: '10px 12px',
  fontSize: '0.92rem',
  fontFamily: theme.typography?.fontFamily || 'serif',
  boxSizing: 'border-box',
});

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.78rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

const helperStyle = (theme) => ({
  marginTop: 4,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.74rem',
  color: theme.colors.textMuted,
});

export const CampaignSettingsModal = ({
  theme,
  campaign,
  extra,
  onClose,
  onSave,
  testCharacters = EMPTY_TEST_CHARACTERS,
  onUpdateTestCharacterPresets = null,
  // Owner-only soft-delete. Container passes a callback that wraps
  // api.campaigns.deleteCampaign; we don't expose the Danger zone at all
  // unless the caller wires this. (Players + DMs see settings without
  // the delete UI; the server-side mutation rejects non-owners anyway.)
  onDelete,
  isOwner = false,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const [tagline, setTagline] = useState('');
  const [schedule, setSchedule] = useState('');
  const [blurb, setBlurb] = useState('');
  const [currentQuest, setCurrentQuest] = useState('');
  const [sessionCount, setSessionCount] = useState('');
  const [enabledFeatures, setEnabledFeatures] = useState(() => ({ ...DEFAULT_FEATURES }));
  const [homebrew, setHomebrew] = useState(() => ({ ...DEFAULT_HOMEBREW }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testPresetCategories, setTestPresetCategories] = useState(() => ({ ...DEFAULT_TEST_PRESET_CATEGORIES }));
  const [testPresetSaving, setTestPresetSaving] = useState(false);
  const [testPresetMessage, setTestPresetMessage] = useState('');
  // Danger-zone state. `dangerOpen` starts collapsed so the destructive
  // input is one extra click away from a stray pointer; `dangerName`
  // must match the campaign's name (case- and whitespace-insensitive,
  // matching the server's normalize check) before the Delete button
  // un-disables.
  const [dangerOpen, setDangerOpen] = useState(false);
  const [dangerName, setDangerName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Seed the form from the current campaign/extra props whenever the modal
  // (re)opens for a different campaign. The dependency on campaignId
  // covers the case where the same modal instance is reused across
  // campaign switches.
  useEffect(() => {
    setTagline(campaign?.tagline || '');
    setSchedule(campaign?.schedule || '');
    setBlurb(campaign?.blurb || '');
    setEnabledFeatures(normalizeFeatureState(campaign?.enabledFeatures));
    setHomebrew(normalizeHomebrewState(campaign?.homebrew));
    setCurrentQuest(extra?.quest || '');
    setSessionCount(
      typeof extra?.sessionCount === 'number' && extra.sessionCount > 0
        ? String(extra.sessionCount)
        : '',
    );
    setError(null);
    setTestPresetCategories(deriveInitialTestPresetCategories(testCharacters));
    setTestPresetSaving(false);
    setTestPresetMessage('');
    setDangerOpen(false);
    setDangerName('');
    setDeleteError(null);
  }, [
    campaign?.id,
    campaign?.tagline,
    campaign?.schedule,
    campaign?.blurb,
    campaign?.enabledFeatures,
    campaign?.homebrew,
    extra?.quest,
    extra?.sessionCount,
    testCharacters,
  ]);

  const normalize = (s) => String(s || '').trim().toLowerCase();
  const dangerNameMatches = normalize(dangerName) === normalize(campaign?.name);

  const handleDelete = async () => {
    if (!onDelete || !campaign?.id) return;
    if (!dangerNameMatches) {
      setDeleteError("That name doesn't match.");
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(campaign.id, dangerName);
      onClose && onClose();
    } catch (err) {
      setDeleteError(err?.message || String(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!onSave || !campaign?.id) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {
        tagline,
        schedule,
        blurb,
        currentQuest,
        enabledFeatures,
        homebrew,
      };
      if (sessionCount === '') {
        // Don't touch sessionCount when blank
      } else {
        const num = Number(sessionCount);
        if (!Number.isFinite(num) || num < 0) {
          throw new Error('Session count must be a non-negative number');
        }
        patch.sessionCount = Math.floor(num);
      }
      await onSave(patch);
      onClose && onClose();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (key) => {
    setEnabledFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleHomebrew = (key) => {
    setHomebrew((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTestPresetCategory = (key) => {
    setTestPresetCategories((prev) => ({ ...prev, [key]: !prev[key] }));
    setTestPresetMessage('');
  };

  const applyTestPresetCategories = async () => {
    if (!onUpdateTestCharacterPresets || !campaign?.id) return;
    setTestPresetSaving(true);
    setTestPresetMessage('');
    try {
      await onUpdateTestCharacterPresets({ testPresetCategories });
      setTestPresetMessage('Updated saved practice presets.');
    } catch (err) {
      setTestPresetMessage(err?.message || String(err));
    } finally {
      setTestPresetSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Campaign settings"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
          <h2 style={{
            margin: 0,
            fontFamily: t.headingFamily,
            fontSize: '1.3rem',
            color: c.accent,
          }}>Campaign Settings</h2>
          <span style={{ color: c.textMuted, fontSize: '0.86rem' }}>{campaign?.name}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle(theme)} htmlFor="csm-tagline">Tagline</label>
            <input
              id="csm-tagline"
              type="text"
              value={tagline}
              maxLength={MAX_TAGLINE}
              placeholder="A short promise for this table"
              onChange={(e) => setTagline(e.target.value)}
              style={fieldStyle(theme)}
            />
            <div style={helperStyle(theme)}>
              {tagline.length} / {MAX_TAGLINE} characters
            </div>
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="csm-schedule">Schedule</label>
            <input
              id="csm-schedule"
              type="text"
              value={schedule}
              maxLength={MAX_SCHEDULE}
              placeholder="Wednesdays · 8pm"
              onChange={(e) => setSchedule(e.target.value)}
              style={fieldStyle(theme)}
            />
            <div style={helperStyle(theme)}>
              Free-form text shown on the campaign card and detail page.
            </div>
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="csm-blurb">Blurb</label>
            <textarea
              id="csm-blurb"
              value={blurb}
              maxLength={MAX_BLURB}
              placeholder="A one- or two-sentence flavor pitch for the campaign."
              rows={3}
              onChange={(e) => setBlurb(e.target.value)}
              style={{ ...fieldStyle(theme), resize: 'vertical', minHeight: 72 }}
            />
            <div style={helperStyle(theme)}>
              {blurb.length} / {MAX_BLURB} characters
            </div>
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="csm-quest">Current Quest</label>
            <input
              id="csm-quest"
              type="text"
              value={currentQuest}
              maxLength={MAX_QUEST}
              placeholder="What the party is chasing right now"
              onChange={(e) => setCurrentQuest(e.target.value)}
              style={fieldStyle(theme)}
            />
          </div>

          <div>
            <label style={labelStyle(theme)} htmlFor="csm-sessions">Session Count</label>
            <input
              id="csm-sessions"
              type="number"
              min="0"
              max="100000"
              step="1"
              value={sessionCount}
              placeholder="e.g. 12"
              onChange={(e) => setSessionCount(e.target.value)}
              style={{ ...fieldStyle(theme), maxWidth: 160 }}
            />
            <div style={helperStyle(theme)}>
              How many sessions you've run so far. Manually set for now; an
              auto-increment is planned alongside the session room feature.
            </div>
          </div>

          <div>
            <div style={labelStyle(theme)}>Campaign Features</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 8,
            }}>
              {FEATURES.map((feature) => (
                <label
                  key={feature.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    border: `1px solid ${theme.colors.borderLight}`,
                    borderRadius: 4,
                    color: theme.colors.textSecondary,
                    fontFamily: t.fontFamily,
                    fontSize: '0.82rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enabledFeatures[feature.key] !== false}
                    onChange={() => toggleFeature(feature.key)}
                  />
                  <span>{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div style={labelStyle(theme)}>Homebrew</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {HOMEBREW_GROUPS.map((group) => (
                <div key={group.key}>
                  <div style={{ ...helperStyle(theme), marginTop: 0, marginBottom: 6 }}>{group.label}</div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 8,
                  }}>
                    {group.items.map((item) => (
                      <label
                        key={item.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: 4,
                          color: theme.colors.textSecondary,
                          fontFamily: t.fontFamily,
                          fontSize: '0.82rem',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={homebrew[item.key] === true}
                          onChange={() => toggleHomebrew(item.key)}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {testCharacters.length > 0 && onUpdateTestCharacterPresets && (
            <div>
              <div style={labelStyle(theme)}>Saved Practice Presets</div>
              <div style={helperStyle(theme)}>
                Legacy saved practice characters only. Real player characters are not changed.
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 8,
                marginTop: 8,
              }}>
                {TEST_PRESET_CATEGORY_OPTIONS.map((option) => (
                  <label
                    key={option.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: 4,
                      color: theme.colors.textSecondary,
                      fontFamily: t.fontFamily,
                      fontSize: '0.82rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={testPresetCategories[option.key] === true}
                      onChange={() => toggleTestPresetCategory(option.key)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={testPresetSaving}
                  onClick={applyTestPresetCategories}
                  style={{
                    padding: '8px 14px',
                    background: 'transparent',
                    color: c.textSecondary,
                    border: `1px solid ${c.borderLight}`,
                    borderRadius: 4,
                    cursor: testPresetSaving ? 'wait' : 'pointer',
                    fontFamily: t.fontFamily,
                    fontSize: '0.84rem',
                  }}
                >
                  {testPresetSaving ? 'Applying...' : `Apply to ${testCharacters.length} saved practice character${testCharacters.length === 1 ? '' : 's'}`}
                </button>
                {testPresetMessage && (
                  <span style={{
                    color: testPresetMessage.toLowerCase().includes('updated') ? c.textMuted : (c.danger || '#d46a5a'),
                    fontFamily: t.fontFamily,
                    fontSize: '0.78rem',
                  }}>
                    {testPresetMessage}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{
            marginTop: 16,
            padding: '8px 12px',
            background: 'rgba(212,106,90,0.15)',
            border: `1px solid ${c.danger || '#d46a5a'}`,
            color: c.danger || '#d46a5a',
            borderRadius: 4,
            fontSize: '0.86rem',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '9px 16px',
              background: 'transparent',
              color: c.textSecondary,
              border: `1px solid ${c.borderLight}`,
              borderRadius: 4,
              cursor: saving ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontSize: '0.88rem',
            }}
          >Cancel</button>
          <button
            type="submit"
            disabled={saving || !campaign?.id}
            style={{
              padding: '9px 18px',
              background: c.accent,
              color: c.textOnAccent || '#000',
              border: 'none',
              borderRadius: 4,
              cursor: saving ? 'wait' : 'pointer',
              fontFamily: t.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>

        {/* Danger zone — owner-only soft-delete. Stays collapsed by default
            so the destructive input never sits one tap away from the
            primary buttons; the user has to deliberately expand it,
            then type the campaign name to unlock Delete. */}
        {isOwner && onDelete && (
          <div style={{
            marginTop: 28,
            paddingTop: 18,
            borderTop: `1px solid ${c.borderLight}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}>
              <div>
                <div style={{
                  fontFamily: t.fontFamily,
                  fontSize: '0.74rem',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: c.danger || '#d46a5a',
                  fontWeight: 700,
                }}>Danger zone</div>
                <div style={{
                  marginTop: 4,
                  fontFamily: t.fontFamily,
                  fontSize: '0.84rem',
                  color: c.textMuted,
                  lineHeight: 1.5,
                  maxWidth: 420,
                }}>
                  Deleting the campaign removes it from every member's
                  dashboard. Roster + announcements + journals stay in
                  storage and can be recovered by an admin.
                </div>
              </div>
              {!dangerOpen && (
                <button
                  type="button"
                  onClick={() => setDangerOpen(true)}
                  style={{
                    padding: '8px 14px',
                    background: 'transparent',
                    color: c.danger || '#d46a5a',
                    border: `1px solid ${c.danger || '#d46a5a'}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: t.fontFamily,
                    fontSize: '0.84rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >Delete campaign…</button>
              )}
            </div>

            {dangerOpen && (
              <div style={{ marginTop: 14 }}>
                <label
                  htmlFor="csm-confirm-delete"
                  style={{
                    display: 'block',
                    marginBottom: 6,
                    fontFamily: t.fontFamily,
                    fontSize: '0.84rem',
                    color: c.textSecondary,
                    lineHeight: 1.5,
                  }}
                >
                  Type <strong style={{ color: c.textPrimary }}>{campaign?.name}</strong> to confirm.
                </label>
                <input
                  id="csm-confirm-delete"
                  type="text"
                  value={dangerName}
                  onChange={(ev) => { setDangerName(ev.target.value); setDeleteError(null); }}
                  placeholder={campaign?.name || ''}
                  autoComplete="off"
                  style={fieldStyle(theme)}
                />
                {deleteError && (
                  <div style={{
                    marginTop: 8,
                    padding: '6px 10px',
                    background: 'rgba(212,106,90,0.15)',
                    border: `1px solid ${c.danger || '#d46a5a'}`,
                    color: c.danger || '#d46a5a',
                    borderRadius: 4,
                    fontSize: '0.82rem',
                  }}>{deleteError}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => { setDangerOpen(false); setDangerName(''); setDeleteError(null); }}
                    disabled={deleting}
                    style={{
                      padding: '7px 14px',
                      background: 'transparent',
                      color: c.textMuted,
                      border: `1px solid ${c.borderLight}`,
                      borderRadius: 4,
                      cursor: deleting ? 'wait' : 'pointer',
                      fontFamily: t.fontFamily,
                      fontSize: '0.84rem',
                    }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting || !dangerNameMatches}
                    style={{
                      padding: '7px 14px',
                      background: dangerNameMatches ? (c.danger || '#d46a5a') : 'transparent',
                      color: dangerNameMatches ? '#fff' : c.danger || '#d46a5a',
                      border: `1px solid ${c.danger || '#d46a5a'}`,
                      borderRadius: 4,
                      cursor: (deleting || !dangerNameMatches) ? 'not-allowed' : 'pointer',
                      fontFamily: t.fontFamily,
                      fontWeight: 700,
                      fontSize: '0.84rem',
                      opacity: (deleting || !dangerNameMatches) ? 0.7 : 1,
                    }}
                  >{deleting ? 'Deleting…' : 'Delete campaign'}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};
